// Which backends this device talks to, set from About: druif (the wine
// catalogue, lib/api.ts) on the build's own address or a custom one, and the
// proef edge (lib/edgeApi.ts), each of which can be switched off. Kept in
// localStorage so the choice survives a reload; every access is guarded, so
// blocked storage only means the choice lasts this session. Untouched, the
// defaults reproduce the build exactly: both on, druif on VITE_API_BASE.
import { useSyncExternalStore } from "react";
import { invalidate } from "./dataCache";

export type DruifMode = "default" | "custom";

export interface Backends {
  readonly druif: {
    readonly enabled: boolean;
    readonly mode: DruifMode;
    readonly customUrl: string;
  };
  readonly edge: { readonly enabled: boolean };
}

/** Why a setter refused its input; About maps it to a translated message. */
export type BackendError = "invalid-url";

const STORAGE_KEY = "proef.backends";

/** Cache prefixes filled from druif (lib/api.ts, lib/outlines.ts): another instance may answer differently. */
const DRUIF_CACHE_PREFIXES = ["wine:", "terroir:", "outline:"] as const;

const DEFAULTS: Backends = {
  druif: { enabled: true, mode: "default", customUrl: "" },
  edge: { enabled: true },
};

function readEnv(key: string): string | undefined {
  const value: unknown = import.meta.env[key];
  return typeof value === "string" ? value : undefined;
}

/**
 * The address a typed URL resolves to, or undefined when it is not an http(s)
 * URL. Credentials, query and fragment are refused because every request path
 * is appended to it; trailing slashes go for the same reason.
 */
export function normaliseBackendUrl(raw: string): string | undefined {
  let url: URL;
  try {
    url = new URL(raw.trim());
  } catch {
    return undefined;
  }
  if (url.protocol !== "http:" && url.protocol !== "https:") return undefined;
  if (url.username !== "" || url.password !== "" || url.search !== "" || url.hash !== "")
    return undefined;
  return `${url.origin}${url.pathname}`.replace(/\/+$/, "");
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/** Whatever was stored, folded onto the defaults field by field; a custom URL that no longer validates falls back to the build default. */
function parse(raw: string | null): Backends {
  if (raw === null) return DEFAULTS;
  let stored: unknown;
  try {
    stored = JSON.parse(raw);
  } catch {
    return DEFAULTS;
  }
  if (!isRecord(stored)) return DEFAULTS;
  const druif = isRecord(stored["druif"]) ? stored["druif"] : {};
  const edge = isRecord(stored["edge"]) ? stored["edge"] : {};
  const customUrl =
    typeof druif["customUrl"] === "string" ? (normaliseBackendUrl(druif["customUrl"]) ?? "") : "";
  return {
    druif: {
      enabled: typeof druif["enabled"] === "boolean" ? druif["enabled"] : true,
      mode: druif["mode"] === "custom" && customUrl !== "" ? "custom" : "default",
      customUrl,
    },
    edge: { enabled: typeof edge["enabled"] === "boolean" ? edge["enabled"] : true },
  };
}

function read(): Backends {
  try {
    return parse(window.localStorage.getItem(STORAGE_KEY));
  } catch {
    // Storage blocked (private mode): the defaults.
    return DEFAULTS;
  }
}

let current: Backends = typeof window === "undefined" ? DEFAULTS : read();
const listeners = new Set<() => void>();

function commit(next: Backends): void {
  const druifMoved =
    next.druif.enabled !== current.druif.enabled ||
    getDruifBaseOf(next) !== getDruifBaseOf(current);
  current = next;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Not persisting is fine: the choice lasts this session.
  }
  if (druifMoved)
    void Promise.all(DRUIF_CACHE_PREFIXES.map((prefix) => invalidate(prefix))).catch(
      () => undefined,
    );
  listeners.forEach((l) => l());
}

function getDruifBaseOf(state: Backends): string {
  return state.druif.mode === "custom" ? state.druif.customUrl : (readEnv("VITE_API_BASE") ?? "");
}

export function getBackends(): Backends {
  return current;
}

/** druif's base URL: the build's (relative "" unless VITE_API_BASE says otherwise) or the validated custom one. */
export function getDruifBase(): string {
  return getDruifBaseOf(current);
}

/** The edge's base URL; only the build sets it (VITE_EDGE_API_BASE). */
export function getEdgeBase(): string {
  return readEnv("VITE_EDGE_API_BASE") ?? "";
}

/**
 * Whether this build has a druif to ask: one served beside it (base /proef/, same-origin /api), one
 * it was built to call (VITE_API_BASE), the dev proxy, or an address typed in About. The standalone
 * Pages build has none (a public origin cannot reach a LAN host), so the device catalogue answers.
 */
export function isDruifOffered(): boolean {
  return (
    current.druif.mode === "custom" ||
    import.meta.env.DEV ||
    import.meta.env.BASE_URL === "/proef/" ||
    (readEnv("VITE_API_BASE") ?? "") !== ""
  );
}

export function isDruifEnabled(): boolean {
  return current.druif.enabled && isDruifOffered();
}

export function isEdgeEnabled(): boolean {
  return current.edge.enabled;
}

export function setDruifEnabled(enabled: boolean): void {
  commit({ ...current, druif: { ...current.druif, enabled } });
}

/** Back to the build's druif; the custom URL is kept so switching back needs no retyping. */
export function setDruifDefault(): void {
  commit({ ...current, druif: { ...current.druif, mode: "default" } });
}

/** Points druif at `raw`; an invalid URL changes nothing and says why. */
export function setDruifCustomUrl(raw: string): BackendError | undefined {
  const customUrl = normaliseBackendUrl(raw);
  if (customUrl === undefined) return "invalid-url";
  commit({ ...current, druif: { ...current.druif, mode: "custom", customUrl } });
  return undefined;
}

export function setEdgeEnabled(enabled: boolean): void {
  commit({ ...current, edge: { enabled } });
}

export function subscribeBackends(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useBackends(): Backends {
  return useSyncExternalStore(subscribeBackends, getBackends, () => DEFAULTS);
}

/** Test seam: forget the stored choice and go back to the defaults. */
export function resetBackends(): void {
  current = DEFAULTS;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing stored to forget.
  }
  listeners.forEach((l) => l());
}

/** Test seam: re-read storage as a fresh page load would. */
export function reloadBackends(): void {
  current = read();
  listeners.forEach((l) => l());
}
